// ── VenueCredStatus — always-on per-venue credential/connection dots ──
//
// Two tiny K / P pills with a status dot each, for the persistent chrome
// (KeyModeBanner strip + StatusBar footer). Now that a venue outage
// DEGRADES instead of blocking (Jul-9 Kalshi-maintenance lesson), the UI
// needs an always-visible answer to "which venue is actually up right
// now" — quiet enough to ignore, present enough to notice when a dot
// isn't green (Brendan, Jul-9).
//
// States:
//   Kalshi — green: connected with a live balance read;
//            amber: connected but the balance probe failed (degraded —
//                   e.g. the Thursday 3–5am ET maintenance pause);
//            red:   credential/connection error;
//            gray:  connecting / not configured.
//   Poly   — green: org pool (or personal connection) answering;
//            amber: relay answered "no poly path" (unconfigured/geoblock);
//            gray:  still resolving.
//   US     — Polymarket US (V55, org-pooled key). Hidden when the relay lane
//            is off; green: the org key is on file and the read context is
//            clean; amber: no org key (NO_POLY_US_CRED) or an adverse
//            context; gray: resolving. Sits between K and P (VENUE_ORDER).

import { useEffect, useState } from 'react';
import { useAccountStore } from '@/stores/accountStore';
import { usePolyBalance } from '@/hooks/usePolyBalance';
import { usePolymarketUsLaneStore } from '@/stores/polymarketUsLaneStore';
import { fetchPolymarketUsStatus, PolymarketUsRelayError, type PolymarketUsStatus } from '@/lib/polymarketUs/relay';

type Tone = 'green' | 'amber' | 'red' | 'gray';

const DOT: Record<Tone, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-400',
  red: 'bg-red-500',
  gray: 'bg-gray-500',
};

function Pill({ label, tone, title }: { label: string; tone: Tone; title: string }) {
  return (
    <span
      className="text-muted-foreground inline-flex items-center gap-1 font-mono text-[10px]"
      title={title}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${DOT[tone]}`} />
      {label}
    </span>
  );
}

// ── Polymarket US status (shared, refreshed on an interval) ──────────────
// One relay read every SHARE_TTL for all chrome instances; the result is
// module-cached so late-mounting pills adopt it synchronously.

type UsStatusResult =
  | { kind: 'ok'; status: PolymarketUsStatus }
  | { kind: 'unavailable'; code: string | null }
  | { kind: 'error' };

const US_SHARE_TTL_MS = 60_000;
let _usCached: { at: number; result: UsStatusResult } | null = null;
let _usInFlight: Promise<UsStatusResult> | null = null;

/** Test seam. */
export function _resetPolymarketUsStatusCacheForTests(): void {
  _usCached = null;
  _usInFlight = null;
}

async function readUsStatus(): Promise<UsStatusResult> {
  try {
    const status = await fetchPolymarketUsStatus();
    return { kind: 'ok', status };
  } catch (err) {
    if (err instanceof PolymarketUsRelayError) return { kind: 'unavailable', code: err.code };
    return { kind: 'error' };
  }
}

export function usePolymarketUsStatusPill(): UsStatusResult | null {
  const lane = usePolymarketUsLaneStore((s) => s.status);
  const [result, setResult] = useState<UsStatusResult | null>(
    _usCached && Date.now() - _usCached.at < US_SHARE_TTL_MS ? _usCached.result : null,
  );
  useEffect(() => {
    if (lane !== 'on') return;
    let stopped = false;
    const tick = async () => {
      if (_usCached && Date.now() - _usCached.at < US_SHARE_TTL_MS) {
        setResult(_usCached.result);
        return;
      }
      _usInFlight ??= readUsStatus().then((r) => {
        _usCached = { at: Date.now(), result: r };
        _usInFlight = null;
        return r;
      });
      const r = await _usInFlight;
      if (!stopped) setResult(r);
    };
    void tick();
    const id = setInterval(() => void tick(), US_SHARE_TTL_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [lane]);
  return lane === 'on' ? result : null;
}

export function VenueCredStatus() {
  const status = useAccountStore((s) => s.status);
  const balance = useAccountStore((s) => s.balance);
  const keyMode = useAccountStore((s) => s.keyMode);
  const poly = usePolyBalance();
  const lane = usePolymarketUsLaneStore((s) => s.status);
  const us = usePolymarketUsStatusPill();

  let kalshiTone: Tone;
  let kalshiTitle: string;
  if (status === 'connected' && balance !== null) {
    kalshiTone = 'green';
    kalshiTitle = `Kalshi connected (${keyMode === 'org' ? 'org keys' : 'personal keys'})`;
  } else if (status === 'connected') {
    kalshiTone = 'amber';
    kalshiTitle =
      'Kalshi degraded — connected but the balance read is failing. Kalshi pauses trading Thu 3:00–5:00 AM ET for maintenance; Polymarket is unaffected.';
  } else if (status === 'error') {
    kalshiTone = 'red';
    kalshiTitle = 'Kalshi connection error — check Settings (or the Thu 3–5 AM ET maintenance window)';
  } else {
    kalshiTone = 'gray';
    kalshiTitle = status === 'connecting' ? 'Kalshi connecting…' : 'Kalshi not connected';
  }

  let polyTone: Tone;
  let polyTitle: string;
  if (poly.source !== null) {
    polyTone = 'green';
    polyTitle = `Polymarket answering (${poly.source === 'org' ? 'org keys' : 'personal keys'})`;
  } else if (poly.unavailable) {
    polyTone = 'amber';
    polyTitle =
      'Polymarket unavailable — no org poly keys on this server and no personal connection (or the relay egress is geoblocked)';
  } else {
    polyTone = 'gray';
    polyTitle = 'Polymarket status resolving…';
  }

  let usPill: { tone: Tone; title: string } | null = null;
  if (lane === 'on') {
    if (us === null) {
      usPill = { tone: 'gray', title: 'Polymarket US status resolving…' };
    } else if (us.kind === 'ok' && us.status.orgKey && !us.status.credentialContext) {
      usPill = { tone: 'green', title: `Polymarket US org key on file (${us.status.orgKey.label})` };
    } else if (us.kind === 'ok' && !us.status.orgKey) {
      usPill = { tone: 'amber', title: 'Polymarket US — no org key on this server. A manager adds it under Org Settings.' };
    } else if (us.kind === 'ok') {
      usPill = { tone: 'amber', title: `Polymarket US degraded (${us.status.credentialContext})` };
    } else if (us.kind === 'unavailable') {
      usPill = { tone: 'amber', title: `Polymarket US unavailable${us.code ? ` (${us.code})` : ''}` };
    } else {
      usPill = { tone: 'gray', title: 'Polymarket US status check failed' };
    }
  }

  return (
    <span className="inline-flex items-center gap-2.5">
      <Pill label="K" tone={kalshiTone} title={kalshiTitle} />
      {usPill && <Pill label="US" tone={usPill.tone} title={usPill.title} />}
      <Pill label="P" tone={polyTone} title={polyTitle} />
    </span>
  );
}
